import { useEffect, useState } from "react";
import { Anchor, ArrowLeftRight, Building2, Coins, Gem, Landmark, Music, Ship, Swords, Target, Trophy } from "lucide-react";
import { Face } from "@/city3d/Hud";
import { Btn, PANEL, Pill } from "@/hud/kit";
import { ACC, fmt } from "@/hud/util";

// The first visit: a welcome with the four goals and the three ways to earn, then a spotlight on each part of the
// HUD, then the first move. Replayable from the menu or with ?tour=1.
const hud = (e) => [
  { target: "topbar-uxd", acc: "gold", icon: Coins, title: "Your UXD", text: "Your money, and what your dock earns per hour." },
  { target: "action-ships", acc: "blue", icon: Ship, title: "Ships", text: "Send a ship to a berth and collect when it docks. Late ships pay less; some cargo comes in lucky." },
  { target: "action-visit", acc: "green", icon: ArrowLeftRight, title: "Trade", text: `${e.visits.of} a day. Each one boosts that dock +${e.visitors.step}% and opens a mystery crate for you.` },
  { target: "action-duel", acc: "rose", icon: Swords, title: "Harbour Rush", text: "Tap the ships before they sail off, for 30 seconds. One free match a day, or challenge anyone for UXD." },
  { target: "goal-tracker", acc: "blue", icon: Target, title: "Your goals", text: "Your next milestone and today's three tasks. Tap one to go there." },
  { target: "topbar-leaderboard", acc: "grey", icon: Trophy, title: "Leaderboard", text: "This week's top earners move into the mansions every Monday." },
  { target: "topbar-hall", acc: "gold", icon: Landmark, title: "City Hall", text: "The mayor, the race and your ballot. Running costs UXD." },
  { target: "sound-deck", acc: "blue", icon: Music, title: "Sound", text: "Music and the sounds of the city. Fold it away with the arrow." },
];

const shown = (id) => {
  const r = document.querySelector(`[data-testid="${id}"]`)?.getBoundingClientRect();
  return r && r.width > 0 && r.bottom > 0 && r.top < window.innerHeight ? r : null;
};

export function useRect(id) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!id) return setRect(null);
    const find = () => setRect(shown(id));
    find();
    const t = setInterval(find, 300);
    window.addEventListener("resize", find);
    return () => { clearInterval(t); window.removeEventListener("resize", find); };
  }, [id]);
  return rect;
}

const Dots = ({ n, i }) => (
  <span className="mr-auto flex gap-1.5">{Array.from({ length: n }, (_, j) => <span key={j} className={`h-1.5 rounded-full transition-[width] ${j === i ? "w-5 bg-x-accent" : "w-1.5 bg-x-active"}`} />)}</span>
);

function Welcome({ user, econ, onNext, onSkip }) {
  const goals = [
    { acc: "blue", icon: Anchor, name: "The Docks", need: "You're here", get: "A dock with one berth and a few UXD to start." },
    { acc: "green", icon: Building2, name: "Uptown", need: `${fmt(econ.line)} a week`, get: "Rent a house uptown, next to your people." },
    { acc: "rose", icon: Gem, name: "Millionaires' Row", need: `Top ${econ.week.seats} this week`, get: "A mansion for the week, handed out every Monday." },
    { acc: "gold", icon: Landmark, name: "Mayor", need: `${fmt(econ.costs.mayor)} to run`, get: "A tax on every ship, decrees, grants from the treasury, your face on City Hall." },
  ];
  const earn = [
    { acc: "blue", icon: Ship, name: "Ships", text: "Rent your berths" },
    { acc: "green", icon: ArrowLeftRight, name: "Trades", text: "Mystery crates" },
    { acc: "rose", icon: Swords, name: "Duels", text: "Win the pot" },
  ];
  return (
    <div className={`${PANEL} hud-thick pop pointer-events-auto max-h-[92vh] w-[min(94vw,680px)] overflow-y-auto rounded-[28px] p-5 sm:p-6`} data-testid="tour-welcome">
      <div className="flex items-center gap-3">
        <Face p={{ a: user.avatar_url, h: user.h }} size={52} ring={ACC.blue} />
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold leading-tight">Welcome to XField</h1>
          <div className="text-sm text-x-mute">@{user.h}, your dock is open. Grow it and climb the city.</div>
        </div>
      </div>

      <h2 className="mt-5 text-sm font-semibold text-x-mute">The road</h2>
      <ol className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {goals.map((g, i) => (
          <li key={g.name} style={{ "--acc": ACC[g.acc] }} className="acc-card relative flex flex-col rounded-[20px] p-3">
            <span className="flex items-center justify-between">
              <g.icon className="h-6 w-6" strokeWidth={2} />
              <span className="text-sm font-semibold tabular-nums opacity-70">{i + 1}</span>
            </span>
            <span className="mt-2 text-[15px] font-semibold leading-tight">{g.name}</span>
            <Pill acc={g.acc} className="mt-1.5 self-start">{g.need}</Pill>
            <span className="mt-1.5 text-xs leading-snug text-x-ink opacity-75">{g.get}</span>
          </li>
        ))}
      </ol>

      <h2 className="mt-5 text-sm font-semibold text-x-mute">How you earn</h2>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {earn.map((e) => (
          <div key={e.name} style={{ "--acc": ACC[e.acc] }} className="hud-well flex items-center gap-2 rounded-[10px] px-2.5 py-2">
            <e.icon className="acc-text h-5 w-5 shrink-0" strokeWidth={2} />
            <span className="min-w-0 leading-tight"><b className="block text-sm">{e.name}</b><span className="block text-[11px] text-x-mute">{e.text}</span></span>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-2">
        <button type="button" onClick={onSkip} className="mr-auto text-sm font-semibold text-x-mute transition hover:text-x-ink" data-testid="tour-skip-button">Skip</button>
        <Btn className="px-5 py-2.5" onClick={onNext} data-testid="tour-next-button">Show me around</Btn>
      </div>
    </div>
  );
}

function Spot({ step, rect, i, n, onBack, onNext, onSkip }) {
  const w = Math.min(window.innerWidth * 0.92, 340), mid = rect.top + rect.height / 2;
  const left = Math.max(12, Math.min(window.innerWidth - w - 12, rect.left + rect.width / 2 - w / 2));
  const place = mid < window.innerHeight / 2 ? { top: rect.bottom + 16 } : { bottom: window.innerHeight - rect.top + 16 };
  return (
    <>
      <div className="pointer-events-none fixed z-40 rounded-[20px] transition-[left,top,width,height] duration-300"
        style={{ left: rect.left - 6, top: rect.top - 6, width: rect.width + 12, height: rect.height + 12, boxShadow: `0 0 0 9999px var(--x-scrim), 0 0 0 2px ${ACC[step.acc]}` }} />
      <div key={step.target} className={`${PANEL} hud-thick pop pointer-events-auto fixed z-50 rounded-[20px] p-4`} style={{ left, width: w, ...place, "--acc": ACC[step.acc] }} data-testid="tour-card">
        <div className="flex items-start gap-3">
          <step.icon className="acc-text mt-0.5 h-6 w-6 shrink-0" strokeWidth={2} />
          <div className="min-w-0">
            <h2 className="text-lg font-semibold leading-tight" data-testid="tour-title">{step.title}</h2>
            <div className="mt-1 text-sm leading-snug text-x-mute" data-testid="tour-text">{step.text}</div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <Dots n={n} i={i} />
          <button type="button" onClick={onSkip} className="px-1 text-xs font-semibold text-x-mute transition hover:text-x-ink" data-testid="tour-skip-button">Skip</button>
          <Btn tone="soft" className="px-3 py-2" onClick={onBack} data-testid="tour-back-button">Back</Btn>
          <Btn className="px-4 py-2" onClick={onNext} data-testid="tour-next-button">Next</Btn>
        </div>
      </div>
    </>
  );
}

function FirstMove({ me, econ, onDone }) {
  const boat = econ.ships[0];
  return (
    <div className={`${PANEL} hud-thick pop pointer-events-auto w-[min(92vw,400px)] rounded-[28px] p-5 text-center`} data-testid="tour-card">
      <Ship className="mx-auto h-10 w-10 text-x-accent" strokeWidth={1.8} />
      <h2 className="mt-3 text-xl font-semibold" data-testid="tour-title">Your first move</h2>
      <div className="mt-1 text-sm text-x-mute" data-testid="tour-text">Send a {boat.name.toLowerCase()}: it docks in {boat.min} minutes. Then trade with a neighbour.</div>
      <div className="mt-5 flex flex-col gap-2">
        <Btn className="py-2.5" onClick={() => onDone("ship")} data-testid="tour-next-button">Send my first ship</Btn>
        {me.invites_left > 0 && <Btn tone="soft" className="py-2" onClick={() => onDone("invite")} data-testid="tour-invite-button">Invite my people</Btn>}
      </div>
    </div>
  );
}

export function Tour({ me, user, econ, stops, onFocus, onDone }) {
  const [i, setI] = useState(-1);
  const [steps, setSteps] = useState([]);
  const step = steps[i];
  const rect = useRect(step?.target);
  useEffect(() => { if (stops[0]) onFocus(stops[0]); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const start = () => {
    setSteps(hud(econ).filter((s) => shown(s.target)));
    setI(0);
  };
  const skip = () => onDone(false);
  const dim = (children) => (
    <div className="reveal-stage fixed inset-0 z-40 grid place-items-center p-3">{children}</div>
  );

  if (i < 0) return dim(<Welcome user={user} econ={econ} onNext={start} onSkip={skip} />);
  if (i >= steps.length) return dim(<FirstMove me={me} econ={econ} onDone={onDone} />);
  const at = rect ?? { left: window.innerWidth / 2, top: window.innerHeight / 2, right: 0, bottom: window.innerHeight / 2, width: 0, height: 0 };
  return <Spot step={step} rect={at} i={i} n={steps.length} onBack={() => (i ? setI(i - 1) : setI(-1))} onNext={() => setI(i + 1)} onSkip={skip} />;
}
